import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import PageHeader from "../../../core/common/commonComponents/PageHeader";
import { exportToCSV, exportToExcel, } from "../../../core/common/GlobalFunctions/exportTableData";
import StatusFilterHeader from "../../../core/common/commonComponents/StatusFilterHeader";
import { AppDispatch } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import TaxModal from "./modal/taxesModal";
import { deleteTax, fetchAllTaxes, } from "../../../core/data/redux/payroll/TDS/taxes/taxActions";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { message } from "antd";
import { formatDate } from "../../../core/utils/FormatData";
import { decryptData } from "../../../core/utils/encryptUtils";

const Taxes = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const { taxes } = useSelector((state: any) => state.accounting.taxes);


  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];
  const canAdd = permissions?.includes("Add") || permissions?.includes("AllowAll");
  const canEdit = permissions?.includes("Edit") || permissions?.includes("AllowAll");
  const canDelete = permissions?.includes("Delete") || permissions?.includes("AllowAll");
  const canExport = permissions?.includes("Export") || permissions?.includes("AllowAll");

  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType("");
  };

  useEffect(() => {
    dispatch(fetchAllTaxes());
  }, [dispatch]);

  const filtereddata = useMemo(() => {
    const filters =
      selectedStatus && selectedStatus !== "All"
        ? { status: selectedStatus }
        : {};
    return filterData(taxes, filters, "createdAt", "createdAt", selectedSort);
  }, [taxes, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedStatus, selectedSort, taxes]);

  const handleDeleteTax = async (id: number) => {
    try {
      const result = await dispatch(deleteTax(id));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("TDS slab deleted successfully");
        await dispatch(fetchAllTaxes());
        setTableKey((prevKey) => prevKey + 1);
        setIsDeleteModalVisible(false);
      } else {
        message.error((result.payload as { error: string })?.error);
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

    const exportData = filtereddata.map((item: any) => ({
    RegimeName: item.regimeName || "N/A",
    tax_percentage: item.tax_percentage || "N/A",
    min_amount: item.min_amount ||  0,
    max_amount: item.max_amount || 0,
    Gender: item.gender || "N/A",
    age: item.age || 0,
    createdAt: item.createdAt || '',
    Status: item.status || "Inactive",
    modifiedAt: item.modifiedAt || "N/A",
    description: item.description || "N/A",
  }));


  const data = taxes;
  
  const columns = [
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Slab Name",
      dataIndex: "regimeName",
      render: (text: String, record: any) => (
        <div className="d-flex align-items-center">
          <h6 className="fs-14 fw-medium text-gray-9 me-2">{text}</h6>
        </div>
      ),
    },

    {
      title: "Age",
      dataIndex: "age",
      sorter: (a: any, b: any) => a.age.length - b.age.length,
    },
    {
      title: "Min Amount",
      dataIndex: "min_amount",
      sorter: (a: any, b: any) => a.min_amount.length - b.min_amount.length,
    },
    {
      title: "Max Amount",
      dataIndex: "max_amount",
      sorter: (a: any, b: any) => a.max_amount.length - b.max_amount.length,
    },
    {
      title: "Slab Tax Percentage(%)",
      dataIndex: "tax_percentage",
      render: (value: number) => `${value}%`,
      sorter: (a: any, b: any) =>
        a.tax_percentage.length - b.tax_percentage.length,
    },
    {
      title: "Effective Date",
      dataIndex: "effective_date",
      render: (text: string) => formatDate(text),
      sorter: (a: any, b: any) =>
        a.effective_date.length - b.effective_date.length,
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: any) => (
        <span
          className={`badge ${text === "active" ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {text}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div>
          <div className="action-icon d-inline-flex">
            {/* Edit Button */}
            {canEdit && (
              <button
                className="me-2"
                onClick={() => {
                  showModal("edit");
                  setSelectedRowData(record);
                }}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Edit"
              >
                <i className="ti ti-edit" />
              </button>
            )}

            {/* Delete Button */}
            {canDelete && (
              <button
                className="me-2"
                disabled={record.status === "inactive"}
                onClick={() => {
                  setSelectedRowData(record);
                  setIsDeleteModalVisible(true);
                }}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Delete"
              >
                <i className="ti ti-trash" />
              </button>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <PageHeader
            title="TDS Slab"
            breadcrumbs={[
              {
                label: <i className="ti ti-smart-home" />,
                path: all_routes.adminDashboard,
              },
              { label: "Accounting" },
              { label: "TDS Slab List", isActive: true },
            ]}
            // listViewPath={all_routes.clientlist}
            listViewPath="#"
            onExportCSV={canExport ? () => exportToCSV(exportData, "TDSSlabData") : undefined}
            onExportExcel={canExport ? () => exportToExcel(exportData, "TDSSlabData") : undefined}
            onAdd={canAdd ? () => showModal("create") : undefined}
            addLabel="Add TDS Slab"
            exportEnabled={canExport}
            addButtonEnabled={canAdd}
            rightExtra={<CollapseHeader />}
          />
          {/* /Breadcrumb */}
          {/* Policy list */}
          <div className="card">
            <StatusFilterHeader
              title="TDS Slab List"
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filtereddata}
                columns={columns}
                Selection={false}
              />
            </div>
          </div>
          {/* /Policylist list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
      <TaxModal
        modalType={modalType}
        handleCancel={handleCancel}
        isModalVisible={isModalVisible}
        selectedRowData={selectedRowData}
      />
      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.tax_id && handleDeleteTax(selectedRowData.tax_id)
        }
      />
    </>
  );
};

export default Taxes;
